import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';

interface WarningCalloutProps {
  children: ReactNode;
  icon?: ReactNode;
  /** Adds the warning outline (Figma's Data Protection cards); the required-fields banner has none. */
  bordered?: boolean;
  /** Stretches to the height of its row, so side-by-side callouts line up. */
  fullHeight?: boolean;
  /** Inner spacing in theme units; banners use 2 (16px) as in the design. */
  padding?: number;
  /** A row under the icon and text (e.g. a banner's links), so the icon stays beside the text. */
  footer?: ReactNode;
}

/**
 * A warning-tinted box. Light mode uses the design's warning tokens; dark mode tints the dark
 * surface instead of the pale light-mode fill, the way MUI's warning Alert does, so it isn't glaring.
 */
export default function WarningCallout({
  children,
  icon,
  bordered = false,
  fullHeight = false,
  padding = 1,
  footer,
}: WarningCalloutProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tokens = isDarkMode
    ? {
        background: alpha(theme.palette.warning.main, 0.12),
        border: alpha(theme.palette.warning.main, 0.5),
        text: theme.palette.warning.light,
      }
    : {
        background: theme.palette.semantic.surface.warning,
        border: theme.palette.semantic.outline.warning,
        text: theme.palette.semantic.metricText.warning,
      };

  return (
    <Box
      data-testid="warning-callout"
      sx={{
        p: padding,
        borderRadius: 1,
        bgcolor: tokens.background,
        color: tokens.text,
        border: bordered ? `1px solid ${tokens.border}` : 'none',
        height: fullHeight ? '100%' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
      </Stack>
      {footer && <Box sx={{ mt: 1 }}>{footer}</Box>}
    </Box>
  );
}
